import { useState, useEffect, useRef, useCallback } from "react";
import "@/index.css";
import { BrowserRouter, Routes, Route, Link, useLocation } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Menu, X, Phone, Mail, MapPin, Star, Send, MessageCircle, 
  Sparkles, Building2, Shirt, CheckCircle, Clock, Shield, 
  Users, ChevronRight, Facebook, Instagram, Twitter, ArrowUp,
  Droplets, Sun, Home, ChevronDown, Globe, ShieldAlert, Building,
  Camera, ZoomIn, HardHat, KeyRound, Brush, ImagePlus, Trash2
} from "lucide-react";
import ServicePage from "./components/ServicePage";
import AdminDashboard from "./components/AdminDashboard";
import AfterPhotoUpload from "./components/AfterPhotoUpload";
import BeforeAfterSlider from "./components/BeforeAfterSlider";
import CookieConsent from "./components/CookieConsent";
import PrivacyPolicy from "./components/PrivacyPolicy";
import TermsConditions from "./components/TermsConditions";
import BlogPage from "./components/BlogPage";
import BlogArticle from "./components/BlogArticle";
import { LanguageProvider, useLanguage, languageNames } from "./i18n/LanguageProvider";
import LanguageSwitcher from "./i18n/LanguageSwitcher";
import LanguageBanner from "./i18n/LanguageBanner";
import LegacyLangRedirect from "./i18n/LegacyLangRedirect";
import Seo from "./seo/Seo";
import { getPageMeta } from "./seo/siteConfig";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Logo URL
const LOGO_URL = "/logo-clean-force.png";

// Image URLs from design guidelines
const IMAGES = {
  hero: "https://images.unsplash.com/photo-1769701282262-ea008aa5833a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzN8MHwxfHNlYXJjaHwyfHxkYXJrJTIwYWJzdHJhY3QlMjB3YXRlciUyMHJpcHBsZXN8ZW58MHx8fHwxNzc1MzM1MTEzfDA&ixlib=rb-4.1.0&q=85",
  cleaning: "https://images.unsplash.com/photo-1627905646269-7f034dcc5738?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzl8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjBjbGVhbmluZyUyMG9mZmljZSUyMHdvcmtlcnxlbnwwfHx8fDE3NzUzMzUwOTl8MA&ixlib=rb-4.1.0&q=85",
  laundry: "https://images.unsplash.com/photo-1545173168-9f1947eebb7f?w=800",
  building: "https://images.unsplash.com/photo-1630908650794-e76619014675?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHwxfHxjbGVhbiUyMG1vZGVybiUyMGJ1aWxkaW5nJTIwZmFjYWRlfGVufDB8fHx8MTc3NTMzNTExM3ww&ixlib=rb-4.1.0&q=85",
  office: "https://images.unsplash.com/photo-1497366216548-37526070297c?w=800",
  window: "https://images.unsplash.com/photo-1492724219889-989d2306de19?w=800",
  osmosis: "https://images.unsplash.com/photo-1746905205773-3ea50c8cd808?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNTl8MHwxfHNlYXJjaHwxfHx3aW5kb3clMjBjbGVhbmluZyUyMHdhdGVyJTIwZmVkJTIwcG9sZSUyMG9zbW9zaXN8ZW58MHx8fHwxNzc1MzM5NzU1fDA&ixlib=rb-4.1.0&q=85",
  solar: "https://images.unsplash.com/photo-1754619880959-2b0528375883?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzl8MHwxfHNlYXJjaHwxfHxzb2xhciUyMHBhbmVsJTIwY2xlYW5pbmclMjBwcm9mZXNzaW9uYWx8ZW58MHx8fHwxNzc1MzQwMTk4fDA&ixlib=rb-4.1.0&q=85",
  vacation: "https://images.unsplash.com/photo-1759264244726-adde4e4318fc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHwyfHxsdXh1cnklMjB2YWNhdGlvbiUyMGhvbWUlMjBiZWRyb29tJTIwY2xlYW4lMjB3aGl0ZXxlbnwwfHx8fDE3NzUzNDA3Mjh8MA&ixlib=rb-4.1.0&q=85",
  construction: "https://images.unsplash.com/photo-1759694920759-6c5537e501e6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHwxfHxyZW5vdmF0aW9uJTIwY2xlYW5pbmclMjBpbnRlcmlvcnxlbnwwfHx8fDE3NzYzNTUwMDV8MA&ixlib=rb-4.1.0&q=85",
  moveout: "https://images.unsplash.com/photo-1757742690834-aa581b9f53b2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA4Mzl8MHwxfHNlYXJjaHwyfHxlbXB0eSUyMGFwYXJ0bWVudCUyMGRlZXAlMjBjbGVhbmluZyUyMG1vdmUlMjBvdXR8ZW58MHx8fHwxNzc2MzU0OTg1fDA&ixlib=rb-4.1.0&q=85",
  deepclean: "https://images.unsplash.com/photo-1758273238415-01ec03d9ef27?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NjZ8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjBkZWVwJTIwY2xlYW5pbmclMjBob21lJTIwaW50ZXJpb3J8ZW58MHx8fHwxNzc2MzU1Njc1fDA&ixlib=rb-4.1.0&q=85"
};

// Language Switcher - uses Link-based component from i18n/LanguageSwitcher.jsx
// The imported LanguageSwitcher component is used directly in the header

// Navigation Component
const Navigation = ({ activeSection }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const { t } = useLanguage();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const services = [
    { slug: "vakantiehuizen", label: t.services.vacation.title, icon: Home },
    { slug: "kantoorschoonmaak", label: t.services.office.title, icon: Building2 },
    { slug: "industriele-reiniging", label: t.services.industrial.title, icon: Sparkles },
    { slug: "ramenwassen-osmose", label: t.services.osmosis.title, icon: Droplets },
    { slug: "zonnepanelen-reiniging", label: t.services.solar.title, icon: Sun },
    { slug: "wasserijdiensten", label: t.services.laundry.title, icon: Shirt },
    { slug: "glazenwassen", label: t.services.windows.title, icon: CheckCircle },
    { slug: "sanering-desinfectie", label: t.services.sanitation.title, icon: ShieldAlert },
    { slug: "bouwoplevering", label: t.services.construction.title, icon: HardHat },
    { slug: "schoonmaak-na-verhuur", label: t.services.moveout.title, icon: KeyRound },
    { slug: "deepclean", label: t.services.deepclean.title, icon: Brush }
  ];

  const navLinks = [
    { href: "#home", label: t.nav.home },
    { href: "#over-ons", label: t.nav.about },
    { href: "#reviews", label: t.nav.reviews },
    { href: "/blog", label: t.blog.badge, isRoute: true },
    { href: "#offerte", label: t.nav.quote }
  ];

  return (
    <nav 
      data-testid="main-navigation"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "glass py-3" : "bg-transparent py-5"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
        <Link to="/" className="flex items-center" data-testid="logo-link">
          <img src={LOGO_URL} alt="Clean-Force" className="h-6 md:h-8 w-auto" />
        </Link>

        {/* Desktop Navigation */}
        <div className="hidden md:flex items-center gap-8">
          <a
            href="#home"
            data-testid="nav-home"
            className={`text-sm font-medium transition-colors hover:text-[#4DD0E1] ${
              activeSection === "home" ? "text-[#4DD0E1]" : "text-white"
            }`}
          >
            {t.nav.home}
          </a>

          {/* Services Dropdown */}
          <div 
            className="relative"
            onMouseEnter={() => setServicesOpen(true)}
            onMouseLeave={() => setServicesOpen(false)}
          >
            <button
              data-testid="nav-diensten-dropdown"
              className={`text-sm font-medium transition-colors hover:text-[#4DD0E1] flex items-center gap-1 ${
                activeSection === "diensten" ? "text-[#4DD0E1]" : "text-white"
              }`}
            >
              {t.nav.services}
              <ChevronDown size={16} className={`transition-transform ${servicesOpen ? "rotate-180" : ""}`} />
            </button>
            
            <AnimatePresence>
              {servicesOpen && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 10 }}
                  className="absolute top-full left-0 mt-2 w-72 glass rounded-xl overflow-hidden"
                  data-testid="services-dropdown-menu"
                >
                  <div className="py-2">
                    {services.map((service) => (
                      <Link
                        key={service.slug}
                        to={`/diensten/${service.slug}`}
                        className="flex items-center gap-3 px-4 py-3 hover:bg-[#4DD0E1]/10 transition-colors"
                        data-testid={`dropdown-${service.slug}`}
                      >
                        <service.icon size={18} className="text-[#4DD0E1]" />
                        <span className="text-white text-sm">{service.label}</span>
                      </Link>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {navLinks.slice(1).map((link) => (
            link.isRoute ? (
              <Link
                key={link.href}
                to={link.href}
                data-testid={`nav-${link.href.slice(1)}`}
                className="text-sm font-medium transition-colors hover:text-[#4DD0E1] text-white"
              >
                {link.label}
              </Link>
            ) : (
              <a
                key={link.href}
                href={link.href}
                data-testid={`nav-${link.href.slice(1)}`}
                className={`text-sm font-medium transition-colors hover:text-[#4DD0E1] ${
                  activeSection === link.href.slice(1) ? "text-[#4DD0E1]" : "text-white"
                }`}
              >
                {link.label}
              </a>
            )
          ))}

          <LanguageSwitcher />

          <a
            href="#offerte"
            data-testid="nav-offerte-btn"
            className="btn-primary text-sm"
          >
            {t.nav.requestQuote}
          </a>
        </div>

        {/* Mobile Menu Button */}
        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setIsOpen(!isOpen)}
          className="md:hidden text-white p-2"
        >
          {isOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden glass mt-2 mx-4 rounded-lg overflow-hidden"
            data-testid="mobile-menu"
          >
            <div className="p-6 flex flex-col gap-4">
              <div className="flex items-center justify-between">
                <a
                  href="#home"
                  onClick={() => setIsOpen(false)}
                  className="text-white hover:text-[#4DD0E1] transition-colors"
                >
                  {t.nav.home}
                </a>
                <LanguageSwitcher />
              </div>
              
              {/* Mobile Services */}
              <div className="border-t border-white/10 pt-4">
                <p className="text-[#4DD0E1] text-xs uppercase tracking-wider mb-3">{t.nav.services}</p>
                {services.map((service) => (
                  <Link
                    key={service.slug}
                    to={`/diensten/${service.slug}`}
                    onClick={() => setIsOpen(false)}
                    className="flex items-center gap-2 py-2 text-white hover:text-[#4DD0E1] transition-colors text-sm"
                  >
                    <service.icon size={16} />
                    {service.label}
                  </Link>
                ))}
              </div>

              <div className="border-t border-white/10 pt-4">
                {navLinks.slice(1).map((link) => (
                  link.isRoute ? (
                    <Link
                      key={link.href}
                      to={link.href}
                      onClick={() => setIsOpen(false)}
                      className="block py-2 text-white hover:text-[#4DD0E1] transition-colors"
                    >
                      {link.label}
                    </Link>
                  ) : (
                    <a
                      key={link.href}
                      href={link.href}
                      onClick={() => setIsOpen(false)}
                      className="block py-2 text-white hover:text-[#4DD0E1] transition-colors"
                    >
                      {link.label}
                    </a>
                  )
                ))}
              </div>
              
              <a
                href="#offerte"
                onClick={() => setIsOpen(false)}
                className="btn-primary text-center text-sm"
              >
                {t.nav.requestQuote}
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

// Hero Section
const HeroSection = () => {
  const { t } = useLanguage();

  const openChat = () => {
    const chatToggle = document.querySelector('[data-testid="chat-toggle"]');
    if (chatToggle) chatToggle.click();
  };

  return (
    <section
      id="home"
      data-testid="hero-section"
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
    >
      {/* Background */}
      <div className="absolute inset-0">
        <img
          src={IMAGES.hero}
          alt="Background"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/60" />
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 py-32 text-center">
        {/* Price Calculation Banner */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="mb-8 max-w-3xl mx-auto"
          data-testid="price-banner"
        >
          <button
            onClick={openChat}
            data-testid="price-banner-btn"
            className="w-full group relative overflow-hidden rounded-2xl border border-[#4DD0E1]/40 transition-all duration-300 hover:border-[#4DD0E1]/80 hover:shadow-[0_0_30px_rgba(77,208,225,0.15)]"
          >
            <div className="absolute inset-0 bg-gradient-to-r from-[#4DD0E1]/15 via-[#0A0A0A]/90 to-[#4DD0E1]/15" />
            <div className="relative flex items-center justify-between gap-4 px-5 py-3 md:px-8 md:py-4">
              <div className="flex items-center gap-3 md:gap-4">
                <div className="w-10 h-10 md:w-12 md:h-12 rounded-xl bg-[#4DD0E1]/20 flex items-center justify-center flex-shrink-0 group-hover:bg-[#4DD0E1]/30 transition-colors">
                  <MessageCircle className="text-[#4DD0E1]" size={22} />
                </div>
                <div className="text-left">
                  <p className="text-white font-bold text-sm md:text-base">{t.hero.banner}</p>
                  <p className="text-[#A1A1AA] text-xs md:text-sm hidden sm:block">{t.hero.bannerSub}</p>
                </div>
              </div>
              <div className="flex items-center gap-2 bg-[#4DD0E1] text-black font-bold text-xs md:text-sm px-4 py-2 md:px-5 md:py-2.5 rounded-xl flex-shrink-0 group-hover:bg-[#3bc0d1] transition-colors">
                {t.hero.bannerCta}
                <ChevronRight size={16} className="group-hover:translate-x-0.5 transition-transform" />
              </div>
            </div>
          </button>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <span className="inline-flex items-center gap-2 text-[#4DD0E1] text-xs tracking-[0.2em] uppercase font-bold mb-6">
            <Sparkles size={16} />
            {t.hero.badge}
          </span>
          
          <h1 className="text-4xl sm:text-5xl lg:text-7xl font-black tracking-tighter text-white mb-6 leading-tight">
            {t.hero.title1}<br />
            <span className="gradient-text">{t.hero.title2}</span><br />
            {t.hero.title3}
          </h1>
          
          <p className="text-lg sm:text-xl text-[#A1A1AA] max-w-2xl mx-auto mb-10">
            {t.hero.description}
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="#offerte" className="btn-primary" data-testid="hero-cta-primary">
              {t.hero.cta1}
            </a>
            <a href="#diensten" className="btn-secondary" data-testid="hero-cta-secondary">
              {t.hero.cta2}
            </a>
          </div>
        </motion.div>

        {/* Stats */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.3 }}
          className="mt-20 grid grid-cols-3 gap-8 max-w-2xl mx-auto"
        >
          {[
            { number: "2021", label: t.hero.stats.founded },
            { number: "100%", label: t.hero.stats.retention },
            { number: "NL & BE", label: t.hero.stats.activeIn }
          ].map((stat) => (
            <div key={stat.number} className="text-center" data-testid={`stat-${stat.number}`}>
              <div className="text-3xl sm:text-4xl font-black text-[#4DD0E1]">{stat.number}</div>
              <div className="text-sm text-[#A1A1AA] mt-1">{stat.label}</div>
            </div>
          ))}
        </motion.div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-float">
        <div className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center pt-2">
          <div className="w-1.5 h-3 bg-[#4DD0E1] rounded-full animate-bounce" />
        </div>
      </div>
    </section>
  );
};

// Services Section
const ServicesSection = () => {
  const { t } = useLanguage();

  const services = [
    {
      icon: Home,
      title: t.services.vacation.title,
      description: t.services.vacation.description,
      image: IMAGES.vacation,
      slug: "vakantiehuizen"
    },
    {
      icon: Building2,
      title: t.services.office.title,
      description: t.services.office.description,
      image: IMAGES.office,
      slug: "kantoorschoonmaak"
    },
    {
      icon: Sparkles,
      title: t.services.industrial.title,
      description: t.services.industrial.description,
      image: IMAGES.cleaning,
      slug: "industriele-reiniging"
    },
    {
      icon: Droplets,
      title: t.services.osmosis.title,
      description: t.services.osmosis.description,
      image: IMAGES.osmosis,
      slug: "ramenwassen-osmose"
    },
    {
      icon: Sun,
      title: t.services.solar.title,
      description: t.services.solar.description,
      image: IMAGES.solar,
      slug: "zonnepanelen-reiniging"
    },
    {
      icon: Shirt,
      title: t.services.laundry.title,
      description: t.services.laundry.description,
      image: IMAGES.laundry,
      link: "https://www.homecareservice.me",
      slug: "wasserijdiensten"
    },
    {
      icon: CheckCircle,
      title: t.services.windows.title,
      description: t.services.windows.description,
      image: IMAGES.window,
      slug: "glazenwassen"
    },
    {
      icon: ShieldAlert,
      title: t.services.sanitation.title,
      description: t.services.sanitation.description,
      image: "https://images.unsplash.com/photo-1642649238366-9b560a5519b9?crop=entropy&cs=srgb&fm=jpg&w=1200",
      slug: "sanering-desinfectie"
    },
    {
      icon: HardHat,
      title: t.services.construction.title,
      description: t.services.construction.description,
      image: IMAGES.construction,
      slug: "bouwoplevering"
    },
    {
      icon: KeyRound,
      title: t.services.moveout.title,
      description: t.services.moveout.description,
      image: IMAGES.moveout,
      slug: "schoonmaak-na-verhuur"
    },
    {
      icon: Brush,
      title: t.services.deepclean.title,
      description: t.services.deepclean.description,
      image: IMAGES.deepclean,
      slug: "deepclean"
    }
  ];

  return (
    <section id="diensten" data-testid="services-section" className="py-24 bg-[#0A0A0A]">
      <div className="max-w-7xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <span className="text-[#4DD0E1] text-xs tracking-[0.2em] uppercase font-bold">
            {t.services.badge}
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight text-white mt-4">
            {t.services.title1}<br />
            <span className="gradient-text">{t.services.title2}</span>
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {services.map((service, index) => (
            <motion.div
              key={service.slug}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              data-testid={`service-card-${service.slug}`}
              className="group relative overflow-hidden rounded-2xl bg-[#121212] border border-white/10 card-hover"
            >
              {service.link ? (
                <a href={service.link} target="_blank" rel="noopener noreferrer" className="block">
                  <div className="aspect-video relative overflow-hidden">
                    <img
                      src={service.image}
                      alt={service.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#121212] to-transparent" />
                  </div>
                  <div className="p-6 relative">
                    <div className="w-12 h-12 rounded-xl bg-[#4DD0E1]/10 flex items-center justify-center mb-4">
                      <service.icon className="text-[#4DD0E1]" size={24} />
                    </div>
                    <h3 className="text-xl font-bold text-white mb-2 flex items-center gap-2">
                      {service.title}
                      <ChevronRight size={18} className="text-[#4DD0E1]" />
                    </h3>
                    <p className="text-[#A1A1AA]">{service.description}</p>
                  </div>
                </a>
              ) : (
                <Link to={`/diensten/${service.slug}`} className="block">
                  <div className="aspect-video relative overflow-hidden">
                    <img
                      src={service.image}
                      alt={service.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#121212] to-transparent" />
                  </div>
                  <div className="p-6 relative">
                    <div className="w-12 h-12 rounded-xl bg-[#4DD0E1]/10 flex items-center justify-center mb-4">
                      <service.icon className="text-[#4DD0E1]" size={24} />
                    </div>
                    <h3 className="text-xl font-bold text-white mb-2 flex items-center gap-2">
                      {service.title}
                      <ChevronRight size={18} className="text-[#4DD0E1]" />
                    </h3>
                    <p className="text-[#A1A1AA]">{service.description}</p>
                  </div>
                </Link>
              )}
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Features Section
const FeaturesSection = () => {
  const { t } = useLanguage();

  const features = [
    {
      icon: Shield,
      title: t.features.reliability.title,
      description: t.features.reliability.description
    },
    {
      icon: Clock,
      title: t.features.flexibility.title,
      description: t.features.flexibility.description
    },
    {
      icon: Star,
      title: t.features.quality.title,
      description: t.features.quality.description
    },
    {
      icon: Users,
      title: t.features.satisfaction.title,
      description: t.features.satisfaction.description
    }
  ];

  return (
    <section id="over-ons" data-testid="features-section" className="py-24 bg-[#121212]">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <span className="text-[#4DD0E1] text-xs tracking-[0.2em] uppercase font-bold">
              {t.features.badge}
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight text-white mt-4 mb-6">
              {t.features.title1}<br />
              <span className="gradient-text">{t.features.title2}</span>
            </h2>
            <p className="text-lg text-[#A1A1AA] mb-8">
              {t.features.description}
            </p>
            
            <div className="grid sm:grid-cols-2 gap-6">
              {features.map((feature) => (
                <div
                  key={feature.title}
                  data-testid={`feature-${feature.title}`}
                  className="p-4 rounded-xl border border-white/10 bg-[#0A0A0A]/50 card-hover"
                >
                  <feature.icon className="text-[#4DD0E1] mb-3" size={28} />
                  <h3 className="text-white font-semibold mb-1">{feature.title}</h3>
                  <p className="text-sm text-[#A1A1AA]">{feature.description}</p>
                </div>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="relative"
          >
            <img
              src={IMAGES.building}
              alt="Clean-Force Building"
              className="rounded-2xl w-full"
            />
            <div className="absolute -bottom-6 -left-6 glass p-6 rounded-xl">
              <div className="text-4xl font-black text-[#4DD0E1]">100%</div>
              <div className="text-white font-medium">{t.hero.stats.retention}</div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Testimonials Section with Google Reviews
const TestimonialsSection = () => {
  const [googleReviews, setGoogleReviews] = useState(null);
  const [loading, setLoading] = useState(true);
  const { t } = useLanguage();

  useEffect(() => {
    const fetchGoogleReviews = async () => {
      try {
        const response = await axios.get(`${API}/google-reviews`);
        setGoogleReviews(response.data);
      } catch {
        // Silently fall back to default reviews
      } finally {
        setLoading(false);
      }
    };
    fetchGoogleReviews();
  }, []);

  // Fallback reviews if Google API fails
  const fallbackReviews = [
    {
      author_name: "Jan Jansen",
      rating: 5,
      text: "Clean-Force BV heeft ons kantoor perfect schoongemaakt. Ze zijn professioneel en betrouwbaar.",
      relative_time_description: "1 jaar geleden"
    },
    {
      author_name: "Sophie Vermeulen", 
      rating: 5,
      text: "De wasserijdiensten van Clean-Force zijn uitstekend. Mijn textiel is altijd perfect verzorgd.",
      relative_time_description: "6 maanden geleden"
    },
    {
      author_name: "Peter de Vries",
      rating: 5,
      text: "Al jaren onze vaste partner voor kantoorschoonmaak. Altijd stipt, altijd grondig.",
      relative_time_description: "2 jaar geleden"
    }
  ];

  const reviews = googleReviews?.reviews || fallbackReviews;
  const overallRating = googleReviews?.overall_rating || 5;
  const totalReviews = googleReviews?.total_reviews || 3;

  return (
    <section id="reviews" data-testid="testimonials-section" className="py-24 bg-[#0A0A0A]">
      <div className="max-w-7xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <span className="text-[#4DD0E1] text-xs tracking-[0.2em] uppercase font-bold">
            {t.reviews.badge}
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight text-white mt-4">
            {t.reviews.title1}<br />
            <span className="gradient-text">{t.reviews.title2}</span>
          </h2>
          
          {/* Overall Rating */}
          <div className="mt-6 flex items-center justify-center gap-4" data-testid="overall-rating">
            <div className="flex gap-1">
              {[...Array(5)].map((_, i) => (
                <Star 
                  key={i} 
                  size={24} 
                  className={i < Math.round(overallRating) ? "fill-[#FBBC04] text-[#FBBC04]" : "text-gray-600"} 
                />
              ))}
            </div>
            <span className="text-2xl font-bold text-white">{overallRating.toFixed(1)}</span>
            <span className="text-[#A1A1AA]">({totalReviews} {t.reviews.reviewsOn})</span>
          </div>

          {/* Google Attribution */}
          <div className="mt-4 flex items-center justify-center gap-2">
            <svg viewBox="0 0 24 24" className="w-5 h-5" xmlns="http://www.w3.org/2000/svg">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            <span className="text-sm text-[#A1A1AA]">{t.reviews.poweredBy}</span>
          </div>
        </motion.div>

        {loading ? (
          <div className="flex justify-center">
            <div className="w-8 h-8 border-2 border-[#4DD0E1] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <div className="grid md:grid-cols-3 gap-6">
            {reviews.map((review) => (
              <motion.div
                key={review.author_name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                data-testid={`testimonial-${review.author_name}`}
                className="p-6 rounded-2xl bg-[#121212] border border-white/10 card-hover"
              >
                <div className="flex gap-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star 
                      key={`review-star-${review.author_name}-${i}`} 
                      size={18} 
                      className={i < review.rating ? "fill-[#FBBC04] text-[#FBBC04]" : "text-gray-600"} 
                    />
                  ))}
                </div>
                <p className="text-[#A1A1AA] mb-6 italic min-h-[60px]">
                  {review.text ? `"${review.text}"` : <span className="text-[#A1A1AA]/50">—</span>}
                </p>
                <div className="flex items-center gap-3">
                  {review.profile_photo_url ? (
                    <img 
                      src={review.profile_photo_url} 
                      alt={review.author_name}
                      className="w-10 h-10 rounded-full object-cover"
                    />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-[#4DD0E1]/20 flex items-center justify-center">
                      <span className="text-[#4DD0E1] font-bold">{review.author_name[0]}</span>
                    </div>
                  )}
                  <div>
                    <div className="text-white font-semibold">{review.author_name}</div>
                    <div className="text-sm text-[#A1A1AA]">{review.relative_time_description}</div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}

        {/* Link to Google Reviews */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mt-10"
        >
          <a
            href="https://www.google.com/maps/place/Clean-Force+BV"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="google-reviews-link"
            className="inline-flex items-center gap-2 text-[#4DD0E1] hover:text-[#3bc0d1] transition-colors"
          >
            {t.reviews.viewAll}
            <ChevronRight size={18} />
          </a>
        </motion.div>

        {/* QR Code Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-16 p-8 rounded-2xl bg-[#121212] border border-white/10 max-w-md mx-auto text-center"
          data-testid="qr-code-section"
        >
          <h3 className="text-xl font-bold text-white mb-2">{t.reviews.qrTitle}</h3>
          <p className="text-[#A1A1AA] mb-6">{t.reviews.qrDescription}</p>
          <div className="bg-[#0A0A0A] p-4 rounded-xl inline-block mb-4">
            <img 
              src={`${API}/review-qr`}
              alt="QR Code"
              className="w-48 h-48"
              data-testid="review-qr-code"
            />
          </div>
          <p className="text-sm text-[#A1A1AA]">
            <a 
              href="https://search.google.com/local/writereview?placeid=ChIJ0_gtudJBwUcRtS9-JeYoNOI"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#4DD0E1] hover:underline"
              data-testid="write-review-link"
            >
              {t.reviews.qrLink}
            </a>
          </p>
        </motion.div>
      </div>
    </section>
  );
};

// Portfolio Section
const PORTFOLIO_IMAGES = [
  {
    src: "https://customer-assets.emergentagent.com/job_clean-relaunch-1/artifacts/dlj33iti_20241212_133706.jpg",
    alt: "Badkamer sanering - overzicht"
  },
  {
    src: "https://customer-assets.emergentagent.com/job_clean-relaunch-1/artifacts/ot6p6lbf_20241212_133715.jpg",
    alt: "Sanitair desinfectie - toilet en ketel"
  },
  {
    src: "https://customer-assets.emergentagent.com/job_clean-relaunch-1/artifacts/8q3ol5bc_20241212_124845.jpg",
    alt: "Toilet reiniging en desinfectie"
  },
  {
    src: "https://customer-assets.emergentagent.com/job_clean-relaunch-1/artifacts/1cnki9c0_20241211_114936.jpg",
    alt: "Radiator en raam schoonmaak"
  },
  {
    src: "https://customer-assets.emergentagent.com/job_clean-relaunch-1/artifacts/vuwkqio8_20241211_114930.jpg",
    alt: "Badkamer sanering - bad en vloer"
  },
  {
    src: "https://customer-assets.emergentagent.com/job_clean-relaunch-1/artifacts/i8nfj1as_image-5.png",
    alt: "Deep Clean keuken - VOOR"
  },
  {
    src: "https://customer-assets.emergentagent.com/job_clean-relaunch-1/artifacts/b93z4b99_image-4.png",
    alt: "Deep Clean keuken - NA"
  }
];

const PortfolioSection = () => {
  const { t } = useLanguage();
  const [lightbox, setLightbox] = useState(null);

  return (
    <section id="portfolio" data-testid="portfolio-section" className="py-24 bg-[#121212]">
      <div className="max-w-7xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <span className="text-[#4DD0E1] text-xs tracking-[0.2em] uppercase font-bold">
            {t.portfolio.badge}
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight text-white mt-4">
            {t.portfolio.title1}{" "}
            <span className="gradient-text">{t.portfolio.title2}</span>
          </h2>
          <p className="text-[#A1A1AA] mt-4 max-w-2xl mx-auto">
            {t.portfolio.description}
          </p>
        </motion.div>

        {/* Masonry-style grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {PORTFOLIO_IMAGES.map((img, idx) => (
            <motion.div
              key={img.src}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: idx * 0.1 }}
              className={`relative group cursor-pointer overflow-hidden rounded-xl border border-white/10 ${
                idx === 0 ? "md:col-span-2 md:row-span-2" : ""
              }`}
              onClick={() => setLightbox(idx)}
              data-testid={`portfolio-image-${idx}`}
            >
              <img
                src={img.src}
                alt={img.alt}
                className={`w-full object-cover transition-transform duration-500 group-hover:scale-105 ${
                  idx === 0 ? "h-64 md:h-full" : "h-48 md:h-64"
                }`}
              />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors duration-300 flex items-center justify-center">
                <ZoomIn className="text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" size={32} />
              </div>
              <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-black/80 to-transparent">
                <span className="inline-flex items-center gap-1.5 text-xs text-[#4DD0E1] font-medium">
                  <Camera size={12} />
                  {t.portfolio.category}
                </span>
              </div>
            </motion.div>
          ))}
        </div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mt-12"
        >
          <p className="text-[#A1A1AA] mb-4">{t.portfolio.cta}</p>
          <a href="#offerte" className="btn-primary inline-flex items-center gap-2" data-testid="portfolio-cta">
            {t.portfolio.ctaButton}
            <ChevronRight size={18} />
          </a>
        </motion.div>
      </div>

      {/* Lightbox */}
      <AnimatePresence>
        {lightbox !== null && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-4"
            onClick={() => setLightbox(null)}
            data-testid="portfolio-lightbox"
          >
            <button
              className="absolute top-6 right-6 text-white/70 hover:text-white z-10"
              onClick={() => setLightbox(null)}
              data-testid="lightbox-close"
            >
              <X size={32} />
            </button>
            <motion.img
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              src={PORTFOLIO_IMAGES[lightbox].src}
              alt={PORTFOLIO_IMAGES[lightbox].alt}
              className="max-w-full max-h-[85vh] object-contain rounded-lg"
              onClick={(e) => e.stopPropagation()}
            />
            {/* Nav arrows */}
            {lightbox > 0 && (
              <button
                className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors"
                onClick={(e) => { e.stopPropagation(); setLightbox(lightbox - 1); }}
                data-testid="lightbox-prev"
              >
                <ArrowUp className="rotate-[-90deg]" size={24} />
              </button>
            )}
            {lightbox < PORTFOLIO_IMAGES.length - 1 && (
              <button
                className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors"
                onClick={(e) => { e.stopPropagation(); setLightbox(lightbox + 1); }}
                data-testid="lightbox-next"
              >
                <ArrowUp className="rotate-90" size={24} />
              </button>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

// Before/After Gallery Section
const GallerySection = () => {
  const { t } = useLanguage();
  const [galleryItems, setGalleryItems] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    axios.get(`${API}/gallery`).then(res => {
      setGalleryItems(res.data);
      setLoaded(true);
    }).catch(() => setLoaded(true));
  }, []);

  if (loaded && galleryItems.length === 0) return null;

  const backendBase = process.env.REACT_APP_BACKEND_URL;

  return (
    <section id="gallery" data-testid="gallery-section" className="py-24 bg-[#0A0A0A]">
      <div className="max-w-7xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <span className="text-[#4DD0E1] text-xs tracking-[0.2em] uppercase font-bold">
            {t.gallery?.badge || "Resultaten"}
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight text-white mt-4">
            {t.gallery?.title1 || "Voor &"}{" "}
            <span className="gradient-text">{t.gallery?.title2 || "Na"}</span>
          </h2>
          <p className="text-[#A1A1AA] mt-4 max-w-2xl mx-auto">
            {t.gallery?.description || "Bekijk het verschil dat Clean-Force maakt."}
          </p>
        </motion.div>

        <div className="space-y-16">
          {galleryItems.map((item, idx) => (
            <motion.div
              key={idx}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: idx * 0.15 }}
              data-testid={`gallery-item-${idx}`}
            >
              <div className="text-center mb-4">
                <span className="px-3 py-1 rounded-full bg-[#4DD0E1]/10 text-[#4DD0E1] text-sm font-medium">
                  {item.service_type}
                </span>
                {item.name && (
                  <p className="text-xs text-[#A1A1AA] mt-2">{t.gallery?.permission || "Met toestemming van de klant"}</p>
                )}
              </div>
              <BeforeAfterSlider
                beforeImage={`${backendBase}${item.before_images[0]}`}
                afterImage={`${backendBase}${item.after_images[0]}`}
              />
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mt-12"
        >
          <p className="text-[#A1A1AA] mb-4">{t.gallery?.cta || "Wilt u ook zo'n resultaat?"}</p>
          <a href="#offerte" className="btn-primary inline-flex items-center gap-2" data-testid="gallery-cta">
            {t.gallery?.ctaButton || "Offerte Aanvragen"}
            <ChevronRight size={18} />
          </a>
        </motion.div>
      </div>
    </section>
  );
};

// Quote Form Section
const QuoteFormSection = () => {
  const { t, language } = useLanguage();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    service_type: "",
    frequency: "",
    details: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");
  const [quoteImages, setQuoteImages] = useState([]);
  const [isUploadingQuote, setIsUploadingQuote] = useState(false);
  const quoteFileRef = useRef(null);

  const handleQuoteImageSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    const remaining = 10 - quoteImages.length;
    const toUpload = files.slice(0, remaining);
    if (toUpload.length === 0) return;

    setIsUploadingQuote(true);
    try {
      const fd = new FormData();
      toUpload.forEach((f) => fd.append("files", f));
      const { data } = await axios.post(`${API}/upload`, fd);
      const newImgs = data.files.map((f) => ({
        url: f.url,
        preview: `${process.env.REACT_APP_BACKEND_URL}${f.url}`,
        filename: f.filename,
      }));
      setQuoteImages((prev) => [...prev, ...newImgs]);
    } catch {
      // silent
    } finally {
      setIsUploadingQuote(false);
      if (quoteFileRef.current) quoteFileRef.current.value = "";
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError("");

    try {
      const payload = { ...formData };
      if (quoteImages.length > 0) {
        payload.details = (payload.details || "") + `\n\n[Bijgevoegde foto's: ${quoteImages.map(img => img.url).join(", ")}]`;
      }
      await axios.post(`${API}/quote`, payload);
      setSubmitted(true);
      setFormData({
        name: "",
        email: "",
        phone: "",
        company: "",
        service_type: "",
        frequency: "",
        details: ""
      });
      setQuoteImages([]);
    } catch (err) {
      setError("Error. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const serviceOptions = [
    { value: "vakantiehuizen", label: t.quote.services.vacation },
    { value: "kantoorschoonmaak", label: t.quote.services.office },
    { value: "industriele-reiniging", label: t.quote.services.industrial },
    { value: "ramenwassen-osmose", label: t.quote.services.osmosis },
    { value: "zonnepanelen-reiniging", label: t.quote.services.solar },
    { value: "glazenwassen", label: t.quote.services.windows },
    { value: "wasserijdiensten", label: t.quote.services.laundry },
    { value: "sanering-desinfectie", label: t.services?.sanitation?.title || "Sanering & Desinfectie" },
    { value: "bouwoplevering", label: t.services?.construction?.title || "Bouwoplevering Schoonmaak" },
    { value: "schoonmaak-na-verhuur", label: t.services?.moveout?.title || "Schoonmaak na Verhuur/Verkoop" },
    { value: "deepclean", label: t.services?.deepclean?.title || "Deep Clean Schoonmaak" },
    { value: "anders", label: t.quote.services.other }
  ];

  const frequencyOptions = [
    { value: "op-afroep", label: t.quote.frequencies.onCall },
    { value: "dagelijks", label: t.quote.frequencies.daily },
    { value: "wekelijks", label: t.quote.frequencies.weekly },
    { value: "tweewekelijks", label: t.quote.frequencies.biweekly },
    { value: "maandelijks", label: t.quote.frequencies.monthly },
    { value: "eenmalig", label: t.quote.frequencies.once }
  ];

  return (
    <section id="offerte" data-testid="quote-form-section" className="py-24 bg-[#121212]">
      <div className="max-w-4xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <span className="text-[#4DD0E1] text-xs tracking-[0.2em] uppercase font-bold">
            {t.quote.badge}
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight text-white mt-4">
            {t.quote.title1}<br />
            <span className="gradient-text">{t.quote.title2}</span>
          </h2>
        </motion.div>

        {submitted ? (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-center p-12 rounded-2xl bg-[#0A0A0A] border border-[#4DD0E1]/30"
            data-testid="quote-success"
          >
            <CheckCircle className="mx-auto text-[#4DD0E1] mb-4" size={64} />
            <h3 className="text-2xl font-bold text-white mb-2">{t.quote.success.title}</h3>
            <p className="text-[#A1A1AA]">{t.quote.success.message}</p>
            <button
              onClick={() => setSubmitted(false)}
              className="btn-primary mt-6"
            >
              {t.quote.success.newRequest}
            </button>
          </motion.div>
        ) : (
          <motion.form
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            onSubmit={handleSubmit}
            className="p-8 rounded-2xl bg-[#0A0A0A] border border-white/10"
            data-testid="quote-form"
          >
            {error && (
              <div className="mb-6 p-4 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400">
                {error}
              </div>
            )}

            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block text-white font-medium mb-2">{t.quote.form.name} *</label>
                <input
                  type="text"
                  required
                  data-testid="quote-name"
                  className="input-dark"
                  placeholder={t.quote.form.namePlaceholder}
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-white font-medium mb-2">{t.quote.form.email} *</label>
                <input
                  type="email"
                  required
                  data-testid="quote-email"
                  className="input-dark"
                  placeholder={t.quote.form.emailPlaceholder}
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-white font-medium mb-2">{t.quote.form.phone} *</label>
                <input
                  type="tel"
                  required
                  data-testid="quote-phone"
                  className="input-dark"
                  placeholder={t.quote.form.phonePlaceholder}
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-white font-medium mb-2">{t.quote.form.company}</label>
                <input
                  type="text"
                  data-testid="quote-company"
                  className="input-dark"
                  placeholder={t.quote.form.companyPlaceholder}
                  value={formData.company}
                  onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-white font-medium mb-2">{t.quote.form.serviceType} *</label>
                <select
                  required
                  data-testid="quote-service"
                  className="input-dark"
                  value={formData.service_type}
                  onChange={(e) => setFormData({ ...formData, service_type: e.target.value })}
                >
                  <option value="">{t.quote.form.selectService}</option>
                  {serviceOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-white font-medium mb-2">{t.quote.form.frequency} *</label>
                <select
                  required
                  data-testid="quote-frequency"
                  className="input-dark"
                  value={formData.frequency}
                  onChange={(e) => setFormData({ ...formData, frequency: e.target.value })}
                >
                  <option value="">{t.quote.form.selectFrequency}</option>
                  {frequencyOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="mt-6">
              <label className="block text-white font-medium mb-2">{t.quote.form.details}</label>
              <textarea
                rows={4}
                data-testid="quote-details"
                className="input-dark resize-none"
                placeholder={t.quote.form.detailsPlaceholder}
                value={formData.details}
                onChange={(e) => setFormData({ ...formData, details: e.target.value })}
              />
            </div>

            <div className="mt-6">
              <label className="block text-white font-medium mb-2">
                <ImagePlus size={16} className="inline mr-2" />
                {language === "nl" ? "Foto's toevoegen (optioneel, max 10)" : language === "en" ? "Add photos (optional, max 10)" : language === "fr" ? "Ajouter des photos (optionnel, max 10)" : "Fotos hinzufügen (optional, max 10)"}
              </label>
              <input
                type="file"
                ref={quoteFileRef}
                accept="image/jpeg,image/png,image/webp"
                multiple
                className="hidden"
                onChange={handleQuoteImageSelect}
                data-testid="quote-file-input"
              />
              <button
                type="button"
                onClick={() => quoteFileRef.current?.click()}
                disabled={isUploadingQuote || quoteImages.length >= 10}
                data-testid="quote-upload-btn"
                className="w-full py-4 rounded-xl border-2 border-dashed border-white/10 hover:border-[#4DD0E1]/40 text-[#A1A1AA] hover:text-[#4DD0E1] transition-colors disabled:opacity-30 flex items-center justify-center gap-2"
              >
                {isUploadingQuote ? (
                  <><div className="w-5 h-5 border-2 border-[#4DD0E1] border-t-transparent rounded-full animate-spin" /> Uploaden...</>
                ) : (
                  <><ImagePlus size={20} /> {language === "nl" ? "Klik om foto's te selecteren" : "Click to select photos"}</>
                )}
              </button>
              {quoteImages.length > 0 && (
                <div className="flex flex-wrap gap-3 mt-3">
                  {quoteImages.map((img, i) => (
                    <div key={i} className="relative group">
                      <img src={img.preview} alt="" className="w-20 h-20 object-cover rounded-lg border border-white/20" />
                      <button
                        type="button"
                        onClick={() => setQuoteImages(prev => prev.filter((_, j) => j !== i))}
                        className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X size={14} className="text-white" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              data-testid="quote-submit"
              className="btn-primary w-full mt-8 flex items-center justify-center gap-2"
            >
              {isSubmitting ? (
                t.quote.form.submitting
              ) : (
                <>
                  {t.quote.form.submit}
                  <ChevronRight size={18} />
                </>
              )}
            </button>
          </motion.form>
        )}
      </div>
    </section>
  );
};


// AI Chat Widget
const ChatWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId] = useState(() => `session_${Date.now()}`);
  const [quoteCount, setQuoteCount] = useState(0);
  const [pendingImages, setPendingImages] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const { t, language } = useLanguage();

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  useEffect(() => {
    axios.get(`${API}/quote/count`).then(res => setQuoteCount(res.data.count)).catch(() => {});
  }, []);

  useEffect(() => {
    if (isOpen && messages.length === 0) {
      setMessages([{
        role: "assistant",
        content: t.chat.welcome
      }]);
    }
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps
  // Only trigger on open state change. Welcome text and empty-check are intentionally excluded to prevent re-initialization.

  const handleImageSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    const remaining = 10 - pendingImages.length;
    const toUpload = files.slice(0, remaining);
    if (toUpload.length === 0) return;

    setIsUploading(true);
    try {
      const formData = new FormData();
      toUpload.forEach((f) => formData.append("files", f));
      const { data } = await axios.post(`${API}/upload`, formData);
      const newImgs = data.files.map((f) => ({
        url: f.url,
        preview: `${process.env.REACT_APP_BACKEND_URL}${f.url}`,
        filename: f.filename,
      }));
      setPendingImages((prev) => [...prev, ...newImgs]);
    } catch {
      // silent
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const removePendingImage = (idx) => {
    setPendingImages((prev) => prev.filter((_, i) => i !== idx));
  };

  const sendMessage = async () => {
    if ((!input.trim() && pendingImages.length === 0) || isLoading) return;

    const userMessage = input.trim();
    const imageUrls = pendingImages.map((img) => img.url);
    const imagePreviews = pendingImages.map((img) => img.preview);
    setInput("");
    setPendingImages([]);
    setMessages(prev => [...prev, { role: "user", content: userMessage || (imageUrls.length > 0 ? `[${imageUrls.length} foto('s) bijgevoegd]` : ""), images: imagePreviews }]);
    setIsLoading(true);

    try {
      const response = await axios.post(`${API}/chat`, {
        session_id: sessionId,
        message: userMessage || "Bekijk deze foto's en geef een prijsinschatting voor het schoonmaakwerk.",
        language: language,
        images: imageUrls,
      });
      setMessages(prev => [...prev, { role: "assistant", content: response.data.response }]);
      if (response.data.response.includes("✅")) {
        axios.get(`${API}/quote/count`).then(res => setQuoteCount(res.data.count)).catch(() => {});
      }
    } catch (err) {
      const isRateLimit = err.response?.status === 429;
      const rateLimitMsg = {
        nl: "De chatbot is even overbelast. Probeer het over een minuut opnieuw.",
        en: "The chatbot is temporarily overloaded. Please try again in a minute.",
        fr: "Le chatbot est temporairement surchargé. Réessayez dans une minute.",
        de: "Der Chatbot ist vorübergehend überlastet. Versuchen Sie es in einer Minute erneut."
      };
      setMessages(prev => [...prev, { 
        role: "assistant", 
        content: isRateLimit ? (rateLimitMsg[language] || rateLimitMsg.nl) : t.chat.error
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="chat-bubble" data-testid="chat-widget">
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            className="absolute bottom-20 right-0 w-[500px] max-w-[calc(100vw-48px)] rounded-2xl overflow-hidden glass border border-white/10"
            data-testid="chat-window"
          >
            <div className="bg-[#4DD0E1] p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                <Sparkles className="text-white" size={20} />
              </div>
              <div>
                <h4 className="text-black font-bold">{t.chat.title}</h4>
                <p className="text-black/70 text-sm">{t.chat.online}</p>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                className="ml-auto text-black/70 hover:text-black"
                data-testid="chat-close"
              >
                <X size={20} />
              </button>
            </div>

            <div className="h-96 overflow-y-auto p-4 space-y-4 bg-[#0A0A0A]" data-testid="chat-messages">
              {messages.map((msg, index) => (
                <div
                  key={`${msg.role}-${index}`}
                  className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
                >
                  <div
                    className={`max-w-[80%] p-3 rounded-2xl ${
                      msg.role === "user"
                        ? "bg-[#4DD0E1] text-black rounded-br-md"
                        : "bg-[#121212] text-white border border-white/10 rounded-bl-md"
                    }`}
                  >
                    {msg.images && msg.images.length > 0 && (
                      <div className="flex flex-wrap gap-1 mb-2">
                        {msg.images.map((src, i) => (
                          <img key={i} src={src} alt="" className="w-16 h-16 object-cover rounded-lg border border-white/20" />
                        ))}
                      </div>
                    )}
                    <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                  </div>
                </div>
              ))}
              {isLoading && (
                <div className="flex justify-start">
                  <div className="bg-[#121212] text-white border border-white/10 p-3 rounded-2xl rounded-bl-md">
                    <div className="flex gap-1">
                      <span className="w-2 h-2 bg-[#4DD0E1] rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                      <span className="w-2 h-2 bg-[#4DD0E1] rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                      <span className="w-2 h-2 bg-[#4DD0E1] rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
                    </div>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            <div className="p-4 bg-[#121212] border-t border-white/10">
              {pendingImages.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-3">
                  {pendingImages.map((img, i) => (
                    <div key={i} className="relative group">
                      <img src={img.preview} alt="" className="w-14 h-14 object-cover rounded-lg border border-white/20" />
                      <button
                        onClick={() => removePendingImage(i)}
                        className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                        data-testid={`remove-image-${i}`}
                      >
                        <X size={12} className="text-white" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <div className="flex gap-2">
                <input
                  type="file"
                  ref={fileInputRef}
                  accept="image/jpeg,image/png,image/webp"
                  multiple
                  className="hidden"
                  onChange={handleImageSelect}
                  data-testid="chat-file-input"
                />
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isLoading || isUploading || pendingImages.length >= 10}
                  data-testid="chat-upload-btn"
                  className="px-3 py-2 rounded-xl bg-white/5 text-[#A1A1AA] hover:text-[#4DD0E1] hover:bg-white/10 transition-colors disabled:opacity-30"
                  title={language === "nl" ? "Foto's toevoegen" : "Add photos"}
                >
                  {isUploading ? <div className="w-5 h-5 border-2 border-[#4DD0E1] border-t-transparent rounded-full animate-spin" /> : <ImagePlus size={18} />}
                </button>
                <input
                  type="text"
                  data-testid="chat-input"
                  className="input-dark flex-1"
                  placeholder={t.chat.placeholder}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyPress={(e) => e.key === "Enter" && sendMessage()}
                />
                <button
                  onClick={sendMessage}
                  disabled={isLoading || (!input.trim() && pendingImages.length === 0)}
                  data-testid="chat-send"
                  className="btn-primary px-4"
                >
                  <Send size={18} />
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <motion.button
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={() => setIsOpen(!isOpen)}
        data-testid="chat-toggle"
        className={`w-14 h-14 rounded-full flex items-center justify-center shadow-lg transition-colors ${
          isOpen ? "bg-[#3bc0d1]" : "bg-[#4DD0E1] animate-pulse-glow"
        }`}
      >
        {isOpen ? (
          <X className="text-black" size={24} />
        ) : (
          <MessageCircle className="text-black" size={24} />
        )}
      </motion.button>

      {!isOpen && quoteCount > 0 && (
        <div
          className="mt-1.5 px-3 py-1 rounded-full bg-[#121212] border border-white/10 text-center"
          data-testid="quote-counter"
        >
          <span className="text-[10px] font-bold text-[#4DD0E1]">{quoteCount}</span>
          <span className="text-[10px] text-[#A1A1AA] ml-1">
            {language === "nl" ? "offertes" : language === "en" ? "quotes" : language === "fr" ? "devis" : "Angebote"}
          </span>
        </div>
      )}
    </div>
  );
};

// Footer
const Footer = () => {
  const { t } = useLanguage();
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  useEffect(() => {
    if (!document.querySelector('script[src*="moneybird"]')) {
      const s = document.createElement("script");
      s.src = "https://www.moneybird.nl/widget/simplerinvoicing.js";
      s.type = "text/javascript";
      s.defer = true;
      s.async = true;
      document.body.appendChild(s);
    }
  }, []);

  return (
    <footer data-testid="footer" className="py-16 bg-[#121212] border-t border-white/10">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid md:grid-cols-4 gap-12">
          {/* Logo & Description */}
          <div className="md:col-span-2">
            <img src={LOGO_URL} alt="Clean-Force" className="h-8 w-auto mb-4" />
            <p className="text-[#A1A1AA] mb-6 max-w-md">
              {t.footer.description}
            </p>
            <div className="flex gap-4">
              <a
                href="https://www.facebook.com/cleanforce3390/"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="social-facebook"
                className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-[#1877F2] hover:text-white transition-colors text-white"
              >
                <Facebook size={18} />
              </a>
              <a
                href="https://www.instagram.com/byljurgen/"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="social-instagram"
                className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-[#E4405F] hover:text-white transition-colors text-white"
              >
                <Instagram size={18} />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-white font-bold mb-4">{t.footer.quickLinks}</h4>
            <ul className="space-y-2">
              {[
                { href: "#home", label: t.nav.home },
                { href: "#diensten", label: t.nav.services },
                { href: "#over-ons", label: t.nav.about },
                { href: "#reviews", label: t.nav.reviews },
                { href: "/blog", label: t.blog.badge, isRoute: true },
                { href: "#offerte", label: t.nav.quote }
              ].map((link) => (
                <li key={link.href}>
                  {link.isRoute ? (
                    <Link
                      to={link.href}
                      className="text-[#A1A1AA] hover:text-[#4DD0E1] transition-colors"
                    >
                      {link.label}
                    </Link>
                  ) : (
                    <a
                      href={link.href}
                      className="text-[#A1A1AA] hover:text-[#4DD0E1] transition-colors"
                    >
                      {link.label}
                    </a>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-white font-bold mb-4">{t.footer.contact}</h4>
            <ul className="space-y-3 text-[#A1A1AA] text-sm">
              <li className="flex items-start gap-2">
                <MapPin size={14} className="text-[#4DD0E1] mt-0.5 flex-shrink-0" />
                <span>Robert Schumandomein 2<br />6229ES Maastricht, Nederland</span>
              </li>
              <li className="flex items-start gap-2">
                <Building2 size={14} className="text-[#4DD0E1] mt-0.5 flex-shrink-0" />
                <span>Dorpsstraat 16<br />3390 Tielt-Winge, België</span>
              </li>
              <li className="flex items-start gap-2">
                <Phone size={14} className="text-[#4DD0E1] mt-0.5 flex-shrink-0" />
                <span>NL: 085/115.5757<br />NL Mobiel: 06 11 28 43 00<br />BE: 0484/448.854</span>
              </li>
              <li className="flex items-start gap-2">
                <Mail size={14} className="text-[#4DD0E1] mt-0.5 flex-shrink-0" />
                <span>office@clean-force.eu</span>
              </li>
            </ul>
            <div className="mt-4 pt-3 border-t border-white/10 text-xs text-[#A1A1AA] space-y-1">
              <p><span className="text-white font-medium">KVK:</span> 94363803</p>
              <p><span className="text-white font-medium">BTW:</span> NL866749913B01</p>
            </div>
          </div>
        </div>

        <div className="mt-12 pt-8 border-t border-white/10 flex flex-col sm:flex-row justify-between items-center gap-4">
          <div className="flex flex-col sm:flex-row items-center gap-2 sm:gap-4">
            <p className="text-[#A1A1AA] text-sm">
              © 2021 - {new Date().getFullYear()} Clean-Force BV. {t.footer.rights}
            </p>
            <Link
              to="/voorwaarden"
              data-testid="terms-link"
              className="text-[#4DD0E1] hover:text-[#3bc0d1] text-sm transition-colors"
            >
              {t.footer.terms}
            </Link>
            <span className="text-white/20 hidden sm:inline">|</span>
            <Link
              to="/privacy"
              data-testid="privacy-link"
              className="text-[#4DD0E1] hover:text-[#3bc0d1] text-sm transition-colors"
            >
              {t.footer.privacy}
            </Link>
          </div>
          <button
            onClick={scrollToTop}
            data-testid="scroll-to-top"
            className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-[#4DD0E1] hover:text-black transition-colors text-white"
          >
            <ArrowUp size={18} />
          </button>
        </div>
        <div className="mt-6 flex justify-center">
          <div id="moneybird-widget-simplerinvoicing" data-id="WyJDbGVhbi1Gb3JjZSBCLlYuIiwiTkw6S1ZLIiwiOTQzNjM4MDMiLCJubCJd"></div>
        </div>
      </div>
    </footer>
  );
};

// Home Page Component
const HomePage = () => {
  const [activeSection, setActiveSection] = useState("home");

  useEffect(() => {
    const handleScroll = () => {
      const sections = ["home", "diensten", "over-ons", "reviews", "portfolio", "offerte"];
      const scrollPosition = window.scrollY + 100;

      for (const section of sections) {
        const element = document.getElementById(section);
        if (element) {
          const { offsetTop, offsetHeight } = element;
          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            setActiveSection(section);
            break;
          }
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const { language } = useLanguage();
  const meta = getPageMeta("/", language);
  const homeJsonLd = {
    "@context": "https://schema.org",
    "@type": "ProfessionalService",
    name: "Clean-Force BV",
    url: "https://www.clean-force.eu",
    email: "office@clean-force.eu",
    telephone: ["+31851155757", "+32484448854", "+31611284300"],
    areaServed: [
      { "@type": "Country", name: "Netherlands" },
      { "@type": "Country", name: "Belgium" },
    ],
    sameAs: ["https://www.facebook.com/cleanforce3390/"],
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <Seo path="/" lang={language} title={meta.title} description={meta.description} jsonLd={homeJsonLd} />
      <Navigation activeSection={activeSection} />
      <main>
        <HeroSection />
        <ServicesSection />
        <FeaturesSection />
        <TestimonialsSection />
        <PortfolioSection />
        <GallerySection />
        <QuoteFormSection />
      </main>
      <Footer />
      <ChatWidget />
      <CookieConsent />
    </div>
  );
};

// Scroll to top on route change
const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, [pathname]);
  return null;
};

const BlogLayoutWrap = ({ children }) => (
  <div className="min-h-screen bg-[#0A0A0A]">
    <Navigation activeSection="" />
    {children}
    <Footer />
    <ChatWidget />
    <CookieConsent />
  </div>
);

// Route definitions (single source of truth)
const APP_ROUTES = [
  { path: "/", element: <HomePage /> },
  { path: "/diensten/:serviceSlug", element: <ServicePage /> },
  { path: "/blog", element: <BlogLayoutWrap><BlogPage /></BlogLayoutWrap> },
  { path: "/blog/:slug", element: <BlogLayoutWrap><BlogArticle /></BlogLayoutWrap> },
  { path: "/admin", element: <AdminDashboard /> },
  { path: "/upload/:token", element: <AfterPhotoUpload /> },
  { path: "/privacy", element: <PrivacyPolicy /> },
  { path: "/voorwaarden", element: <TermsConditions /> },
];

const LANG_PREFIXES = ["en", "fr", "de"];

// Main App Component with Routing
function App() {
  return (
    <BrowserRouter>
      <LanguageProvider>
        <ScrollToTop />
        <LegacyLangRedirect />
        <LanguageBanner />
        <Routes>
          {/* Dutch routes (default, no prefix) */}
          {APP_ROUTES.map((r) => (
            <Route key={r.path} path={r.path} element={r.element} />
          ))}
          {/* Other language routes with prefix */}
          {LANG_PREFIXES.map((lang) =>
            APP_ROUTES.map((r) => (
              <Route key={`${lang}${r.path}`} path={`/${lang}${r.path === "/" ? "" : r.path}`} element={r.element} />
            ))
          )}
        </Routes>
      </LanguageProvider>
    </BrowserRouter>
  );
}

// Blog Layout with Navigation, Footer, Chat
const BlogLayout = BlogLayoutWrap;

export default App;
